import { useState, useEffect } from "react";
import { CyberpassButton } from "../Input";
import CheckMarkIcon from "../../public/checkMark.svg?react";
import Modal from "../Modal";
import * as api from "../api/customer";
import * as serviceApi from "../api/service";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { useTranslation } from "../i18n";
import { selectAccessToken } from "../store/auth";

/**
 * @param {object} props
 * @param {boolean} props.isActive
 * @param {() => void} props.setActive
 * @param {string} props.title
 */
function ServiceCard({ isActive, setActive, title }) {
  return (
    <div
      className={`cursor-pointer p-4 flex justify-between items-center rounded-lg relative ${isActive ? "bg-accent" : "bg-c-gray-500 "}`}
      onClick={setActive}
    >
      <p className="text-lg">{title}</p>
      <CheckMarkIcon className={`h-7 w-7 ${isActive ? "" : "hidden"}`} />
    </div>
  );
}

/**
 * @param {object} props
 * @param {RegisteredCustomer} props.customer
 * @param {() => void} props.switchToBuyMinutes
 * @returns {React.Component}
 */
function CheckoutMinutes({ customer, switchToBuyMinutes }) {
  const { t } = useTranslation();
  const accessToken = useSelector(selectAccessToken);
  const [showModal, setShowModal] = useState(false);
  const [minutesToCheckout, setMinutesToCheckout] = useState(20);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState();

  useEffect(() => {
    if (accessToken == null) return;
    const inner = async () => {
      const resp = await serviceApi.fetchCurrent(accessToken);
      setServices(resp);
      if (resp.length === 1) {
        setSelectedService(resp[0].service);
      } else {
        setSelectedService(undefined);
      }
    };
    inner();
  }, [accessToken]);

  /**
   * @type {React.ChangeEventHandler<HTMLInputElement>}
   */
  const updateMinutesToCheckout = (evt) => {
    if (evt.target.value.length > 4) {
      return;
    }
    // Cause I can do it
    while (evt.target.value.startsWith("0")) {
      evt.target.value = evt.target.value.slice(1);
    }
    const value = Number(evt.target.value);
    setMinutesToCheckout(Math.max(value, 0));
  };

  const handleWheel = (evt) => {
    evt.preventDefault();
  };

  function checkoutMinutes() {
    if (!selectedService) {
      toast.error(t("toast.selectService"));
      return;
    }
    if (customer.debt > 0) {
      toast.error(t("toast.debtCheckoutProhibited"));
      setMinutesToCheckout(0);
      return;
    }

    toast.promise(
      async () => {
        try {
          const resp = await api.checkoutMinutes(accessToken, {
            customer: { id: customer.id },
            minutesAmount: minutesToCheckout,
            service: selectedService,
            // TODO: Remove deprecated fields
            locationId: 1,
            adminId: 1,
          });
          if (api.isCheckoutMinutesSucceed(resp)) {
            toast.success(t("toast.minutesDeducted"));
          } else {
            toast(t("toast.insufficientFunds"), { icon: "⚠️" });
          }
          switchToBuyMinutes();
          setMinutesToCheckout(0);
        } catch (err) {
          console.error(err);
        }
      },
      {
        loading: t("common.loading"),
        error: t("toast.minutesDeductionFailed"),
      },
    );
  }

  return (
    <div className="grid grid-cols-2 justify-center items-center">
      {services.length > 0 ? (
        <div className="grid gap-4 p-4 grid-cols-2 w-full">
          {services.map(({ service }) => (
            <ServiceCard
              key={service}
              isActive={service === selectedService}
              setActive={() => setSelectedService(service)}
              title={service}
            />
          ))}
        </div>
      ) : (
        <p className="text-center">{t("toast.servicesLoadError")}</p>
      )}
      <div className="h-full p-2 space-y-2 flex flex-col items-center flex-grow bg-c-gray-600 rounded-lg">
        <h1 className="text-c-gray-400">{t("checkout.info")}</h1>
        <div className="h-40 bg-c-gray-500 rounded-lg flex flex-col items-center justify-center">
          <div className="flex flex-row items-center justify-between w-full px-4">
            <button
              className="px-2 py-0.5 bg-c-gray-600 rounded text-gray-300"
              onClick={() => {
                setMinutesToCheckout(Math.max(minutesToCheckout - 20, 0));
              }}
            >
              - 20
            </button>
            <input
              type="number"
              min="0"
              value={minutesToCheckout}
              onChange={updateMinutesToCheckout}
              onWheel={handleWheel}
              className="w-64 text-5xl text-center text-white ring-0 shadow-none bg-c-gray-500 focus:ring-0 focus:border-none focus:shadow-none focus:outline-none"
            />
            <button
              className="px-2 py-0.5 bg-c-gray-600 rounded text-gray-300"
              onClick={() => {
                setMinutesToCheckout(Math.min(minutesToCheckout + 20, 9999));
              }}
            >
              + 20
            </button>
          </div>
        </div>
        <div className="mx-auto">
          <CyberpassButton
            onClick={() => {
              if (customer.debt > 0) {
                toast.error(t("toast.debtCheckoutProhibited2"));
                return;
              }
              setShowModal(true);
            }}
            type="submit"
            disabled={!selectedService}
          >
            <p className="mx-6">{t("common.confirm")}</p>
          </CyberpassButton>
        </div>
      </div>
      <Modal
        title={
          customer.minutesBalance >= minutesToCheckout
            ? t("checkout.confirmTitle")
            : t("toast.insufficientFunds")
        }
        content={
          <div className="">
            {customer.minutesBalance >= minutesToCheckout ? (
              <>
                <p>
                  {t("checkout.willBeDeducted")}{" "}
                  <span className="font-bold">{minutesToCheckout}</span>
                  {t("common.minutes")}
                </p>
                <p>
                  {t("checkout.customerWillHave")}{" "}
                  <span className="font-bold text-orange-500">
                    {customer.minutesBalance - minutesToCheckout}
                  </span>
                  {t("common.minutes")}{" "}
                </p>
                <p>
                  {t("checkout.service")}:{" "}
                  <span className="font-bold text-white">
                    {selectedService}
                  </span>
                </p>
              </>
            ) : (
              <p>
                {t("checkout.insufficientBalance1")}{" "}
                <b>{minutesToCheckout - customer.minutesBalance}</b>{" "}
                {t("common.minutes")}.{t("checkout.insufficientBalance2")}
              </p>
            )}
          </div>
        }
        showModal={showModal}
        setShowModal={setShowModal}
        setModalConfirmed={checkoutMinutes}
      />
    </div>
  );
}

export default CheckoutMinutes;
